/**
 * Central content for the Xlog3D User Manual panel (Guide + local search index).
 *
 * Schema:
 *   - Each top-level entry has a stable `id`, a display `number` (e.g. "2.1"),
 *     a `title`, optional `intro` paragraph, optional `items` (bullets or
 *     {name, desc} pairs), optional `subsections` (recursive), and an
 *     optional `caution` note rendered at the very bottom of the section.
 *   - Anchor element ids follow `helpSectionElementId(id)`.
 *   - `number` is metadata only — the renderer shows `title`, never `number`.
 *
 * Sourced from Xlog3D_User_Manual_Edition.1_Oct26.docx (Edition 1.0, October 2026).
 *
 * Figures are images embedded in that docx, bundled via webpack `require`
 * (same convention as helpers/images.js) so the relative URLs resolve under
 * both the web build and the Electron file:// load. Each is attached to its
 * section as `image: { src, alt }` and rendered after the section's text.
 */

// react-scripts 4 file-loader returns a module object; the URL is on `.default`
// (same as helpers/images.js — every consumer uses `.default`).
const fig01 = require("../../images/handbook/fig-01-terminology.png").default;
const fig02 = require("../../images/handbook/fig-02-weak-zone-log.png").default;
const fig03 = require("../../images/handbook/fig-03-weak-zone-filled.png").default;
const fig04 = require("../../images/handbook/fig-04-orientation-chainages.png").default;
const fig05 = require("../../images/handbook/fig-05-source-3d-log.png").default;
const fig06 = require("../../images/handbook/fig-06-generated-shear-log.png").default;
const fig07a = require("../../images/handbook/fig-07a-face-log-1.jpg").default;
const fig07b = require("../../images/handbook/fig-07b-face-log-2.jpg").default;
const fig08 = require("../../images/handbook/fig-08-face-orientation.png").default;
const fig09 = require("../../images/handbook/fig-09-face-3d-log.png").default;
const fig10 = require("../../images/handbook/fig-10-dominant-sets.jpeg").default;
const fig11 = require("../../images/handbook/fig-11-hrt-desander-adit.png").default;
const fig12 = require("../../images/handbook/fig-12-hrt-adit-surge.png").default;
const fig13 = require("../../images/handbook/fig-13-pressure-shaft.png").default;
const fig14 = require("../../images/handbook/fig-14-powerhouse-cavern.png").default;
const fig15 = require("../../images/handbook/fig-15-surge-tank.png").default;
const fig16 = require("../../images/handbook/fig-16-shear-horizontal-ps.png").default;
const fig17 = require("../../images/handbook/fig-17-shear-inclined-ps.png").default;
const fig17a = require("../../images/handbook/fig-17a-inclined-io-table.png").default;
const fig18 = require("../../images/handbook/fig-18-shear-drop-shaft.png").default;
const fig19 = require("../../images/handbook/fig-19-fault-proposed.png").default;
const fig20 = require("../../images/handbook/fig-20-fault-proposed-filled.png").default;
const fig21 = require("../../images/handbook/fig-21-fault-alternative.png").default;
const fig22 = require("../../images/handbook/fig-22-fault-alternative-filled.png").default;

const inputTerminology = [
  { name: "Ch.", desc: "Chainage or RD (reduced distance) of tunnel." },
  {
    name: "Tunnel Direction",
    desc: "Tunnel direction in azimuth is in the direction of increasing chainage.",
  },
  {
    name: "Tunnel Inclination",
    desc: "Positive (+) when the tunnel falls, negative (−) when it rises, in the direction of increasing chainage.",
  },
  { name: "Height", desc: "Tunnel height from invert to centreline at crown." },
  {
    name: "Width",
    desc: "Tunnel width at SPL (spring level); also at invert level for horse-shoe shaped tunnels.",
  },
  {
    name: "Wall height",
    desc: "Tunnel wall height from invert to SPL (measured vertically).",
  },
  {
    name: "Label",
    desc: "Chosen label for discontinuity like J1, Shear, Fault, etc.",
  },
  { name: "DipDir", desc: "Dip direction of discontinuity." },
  { name: "DipAmt", desc: "Dip amount of discontinuity." },
  {
    name: "Data Point",
    desc: "Location of chosen point on discontinuity for plotting log.",
  },
  {
    name: "Tunnel Surface",
    desc: "Used when data location falls on tunnel face or heading.",
  },
  { name: "Face", desc: "Face or heading of tunnel." },
  {
    name: "Side",
    desc: "Left or right side of data location with reference to centreline of tunnel face.",
  },
  {
    name: "Distance",
    desc: "Distance of the data location from centreline of tunnel face.",
  },
  { name: "Height (data location)", desc: "Height of data location from tunnel invert." },
];

const outputTerminology = [
  { name: "Angle", desc: "Angle between discontinuity strike and tunnel direction." },
  {
    name: "Adip",
    desc: "Apparent dip of discontinuity with horizontal axis (horizontal tunnel).",
  },
  {
    name: "ADipl",
    desc: "Apparent dip of discontinuity with inclined axis (inclined tunnel).",
  },
  { name: "ChLI", desc: "Discontinuity trace Ch. on left invert." },
  { name: "ChRI", desc: "Discontinuity trace Ch. on right invert." },
  { name: "ChLSPL", desc: "Discontinuity trace Ch. on Left SPL (spring level)." },
  { name: "ChRSPL", desc: "Discontinuity trace Ch. on right SPL (spring level)." },
  { name: "ChCL", desc: "Discontinuity trace Ch. on centreline at crown." },
  {
    name: "ChMin",
    desc: "Minimum chainage of the geometrically computed discontinuity trace.",
  },
  {
    name: "ChMax",
    desc: "Maximum chainage of the geometrically computed discontinuity trace.",
  },
  {
    name: "Trace Span",
    desc: "Longitudinal extent of the discontinuity trace, calculated as ChMax − ChMin (not a separate software output).",
  },
  {
    name: "Asf",
    desc: "Apparent Spacing Factor for computing discontinuity apparent spacing and thickness along tunnel.",
  },
];

const commonTunnelInputs = [
  {
    name: "Tunnel Direction",
    desc: "Azimuth of the tunnel direction on the increasing-chainage side. Left and right are referenced while looking in this direction.",
  },
  {
    name: "Tunnel Inclination",
    desc: "Inclination of the tunnel axis in the direction of increasing chainage. Enter 0 for a horizontal tunnel; use a positive value when the tunnel falls and a negative value when it rises in the tunnel direction.",
  },
  {
    name: "Start Chainage",
    desc: "Choose the lesser chainage end of the desired log format. If required, edit chainage to avoid blank space in the log.",
  },
  {
    name: "End Chainage",
    desc: "Choose the higher chainage end of the desired log format. If required, edit chainage to avoid blank space in the log.",
  },
  {
    name: "Dip Direction",
    desc: "True dip direction of the discontinuity, entered as an azimuth in whole degrees (integer).",
  },
  {
    name: "Dip Amount",
    desc: "True dip amount of the discontinuity, entered in whole degrees (integer).",
  },
  {
    name: "Discontinuity Data Location",
    desc: "Chainage of the discontinuity trace at Left Spring Level (SPL), Right Spring Level (SPL), Centreline at Crown, or any other point on the tunnel face/heading.",
  },
  {
    name: "Side of tunnel",
    desc: "Data point location to the left, right or on the centreline while looking in the tunnel direction.",
  },
  {
    name: "Distance from Centreline",
    desc: "Perpendicular horizontal distance of the data point from the tunnel centreline.",
  },
  {
    name: "Vertical Height from Invert",
    desc: "Vertical height of the data point on the discontinuity trace above the invert.",
  },
  {
    name: "Chainage",
    desc: "Chainage (RD) of the selected known point on the discontinuity trace.",
  },
];

const faceLogInputs = [
  {
    name: "Face 1 Chainage",
    desc: "Chainage of the first tunnel face on which the discontinuity trace is mapped.",
  },
  {
    name: "Face 2 Chainage",
    desc: "Chainage of the second tunnel face on which the same discontinuity trace is mapped.",
  },
  {
    name: "Apparent Dip of Discontinuity on Face-1 / Face",
    desc: "Apparent dip of the discontinuity trace on the Face, entered in whole degrees (integer).",
  },
  {
    name: "Trace Direction on Face-1 / Face-2",
    desc: "Enter whether the trace falls or rises towards right of the Face.",
  },
  {
    name: "Trace Points",
    desc: "Enter convenient mapped points on the trace in the two face logs, using the face-position and height references required by the algorithm. The points are used to reconstruct the discontinuity plane geometrically.",
  },
];

const dShapeInputs = [
  {
    name: "Tunnel Height",
    desc: "Maximum vertical height from invert to crown.",
  },
  {
    name: "Tunnel Width",
    desc: "Width of the tunnel at invert/spring level.",
  },
  {
    name: "Wall Height",
    desc: "Vertical height of the spring level above invert.",
  },
  {
    name: "Discontinuity Data Location",
    desc: "Left Invert or Right Invert: data location point on the corresponding invert.",
  },
];

const circularInputs = [
  { name: "Tunnel Radius", desc: "Radius of the circular tunnel." },
  {
    name: "Discontinuity Data Location",
    desc: "Invert: data location point on Invert (Centreline at Invert).",
  },
];

const horseshoeInputs = [
  {
    name: "Tunnel Height",
    desc: "Maximum vertical height from invert to crown.",
  },
  {
    name: "Tunnel Width at Invert",
    desc: "Width of the tunnel at invert level.",
  },
  {
    name: "Tunnel Width at SPL",
    desc: "Width of the tunnel at spring level.",
  },
  {
    name: "Wall Height",
    desc: "Vertical height of the spring level above invert.",
  },
  {
    name: "Discontinuity Data Location",
    desc: "Left Invert or Right Invert: data location point on the corresponding invert.",
  },
];

const shaftInputs = [
  {
    name: "View Direction",
    desc: "Azimuth from which the shaft log is viewed. For hydropower projects, a downstream view may be preferred where appropriate.",
  },
  { name: "Shaft Radius", desc: "Radius of the shaft." },
  {
    name: "Start Depth",
    desc: "Depth at the beginning of the plotted shaft log (upper level). Zero may be used for schematic plotting.",
  },
  {
    name: "End Depth",
    desc: "Depth at the end of the plotted shaft log (lower level).",
  },
  {
    name: "Discontinuity Dip Direction",
    desc: "True dip direction of the discontinuity, entered as azimuth.",
  },
  {
    name: "Discontinuity Dip Amount",
    desc: "True dip amount of the discontinuity.",
  },
  {
    name: "Minimum Depth",
    desc: "Minimum depth of the discontinuity trace on the shaft wall; this represents the up-dip position of the trace.",
  },
];

const commonTunnelOutputs = [
  {
    name: "Crown Radius",
    desc: "Computed radius of the tunnel crown, based on the input tunnel geometry.",
  },
  {
    name: "Crown Circumference",
    desc: "Developed length of the tunnel crown, used for generating the 3D Discontinuity Log.",
  },
  {
    name: "Angle",
    desc: "Acute horizontal angle between the discontinuity strike and tunnel direction.",
  },
  {
    name: "Adip",
    desc: "Apparent dip of the discontinuity along the tunnel direction, referenced to a horizontal axis.",
  },
  {
    name: "ADipl",
    desc: "Apparent dip of the discontinuity relative to the inclined tunnel axis, accounting for tunnel inclination.",
  },
  {
    name: "ChLSPL",
    desc: "Chainage at which the discontinuity trace intersects the Left Spring Level.",
  },
  {
    name: "ChRSPL",
    desc: "Chainage at which the discontinuity trace intersects the Right Spring Level.",
  },
  {
    name: "ChCL",
    desc: "Chainage at which the discontinuity trace intersects the centreline at crown.",
  },
  {
    name: "ChMin",
    desc: "Minimum chainage of the geometrically computed discontinuity trace on the excavation surface.",
  },
  {
    name: "ChMax",
    desc: "Maximum chainage of the geometrically computed discontinuity trace on the excavation surface.",
  },
  {
    name: "Asf (Apparent Spacing Factor)",
    desc: "Excavation-specific factor used to compute apparent spacing and apparent thickness from their respective true values.",
  },
  {
    name: "Alerts",
    desc: "Identifies special geometric conditions, such as a discontinuity trace parallel to the tunnel direction or extending beyond the selected Start or End Chainage of the log.",
  },
];

const derivedOutputs = [
  {
    name: "Trace Span",
    desc: "Longitudinal extent of the discontinuity trace along the excavation, calculated as ChMax − ChMin.",
  },
  {
    name: "Apparent Spacing",
    desc: "Spacing between successive discontinuities measured along the tunnel axis, calculated as Apparent Spacing = True Spacing / Asf.",
  },
  {
    name: "Apparent Thickness",
    desc: "Thickness of a discontinuity or weak zone measured along the tunnel axis, calculated as Apparent Thickness = True Thickness / Asf. True spacing and true thickness may be computed using the TrueSpace web tool at www.dhawanacademy.com.",
  },
];

const hrtDesanderAdit = [
  { name: "Tunnel axis / inclination", desc: "257/00" },
  { name: "Horizontal angle (between tunnel axis & strike)", desc: "44" },
  { name: "Shear zone trace span along tunnel", desc: "5.6 m" },
  {
    name: "Apparent shear-zone spacing along tunnel",
    desc: "127 m (TS / Asf = 75 / 0.59)",
  },
  {
    name: "Apparent thickness of shear zone along tunnel",
    desc: "6.8 m (TT / Asf = 4 / 0.59)",
  },
  { name: "Estimated number of shear zones", desc: "7 (Length / App Spacing = 900 / 127)" },
  { name: "Cumulative shear zone span", desc: "39 m (7 × 5.6)" },
  { name: "Cumulative shear zone thickness", desc: "48 m (7 × 6.8)" },
  {
    name: "Cumulative shear zone affected length along tunnel",
    desc: "87 m (39 + 48)",
  },
  {
    name: "Estimated proportion of Poor–Very Poor rock (Class IV–V)",
    desc: "9.7%",
  },
];

const hrtAditSurge = [
  { name: "Tunnel axis / inclination", desc: "280/00" },
  { name: "Horizontal angle (between tunnel axis & strike)", desc: "67" },
  { name: "Shear zone trace span along tunnel", desc: "3.3 m" },
  {
    name: "Apparent shear-zone spacing along tunnel",
    desc: "96 m (TS / Asf = 75 / 0.78)",
  },
  {
    name: "Apparent thickness of shear zone along tunnel",
    desc: "5.1 m (TT / Asf = 4 / 0.78)",
  },
  {
    name: "Estimated number of shear zones",
    desc: "37 (Length / App Spacing = 3510 / 96)",
  },
  { name: "Cumulative shear zone span", desc: "122 m (37 × 3.3)" },
  { name: "Cumulative shear zone thickness", desc: "189 m (37 × 5.1)" },
  {
    name: "Cumulative shear zone affected length along tunnel",
    desc: "311 m (122 + 189)",
  },
  {
    name: "Estimated proportion of Poor–Very Poor rock (Class IV–V)",
    desc: "8.9%",
  },
];

const pressureShaftAssessment = [
  { name: "Tunnel axis / inclination", desc: "269/15" },
  { name: "Horizontal angle (between tunnel axis & strike)", desc: "56" },
  { name: "Shear zone trace span along tunnel", desc: "3.2 m" },
  {
    name: "Apparent shear zone spacing along tunnel",
    desc: "139 m (TS / Asf = 75 / 0.54)",
  },
  {
    name: "Apparent thickness of shear zone along tunnel",
    desc: "7.4 m (TT / Asf = 4 / 0.54)",
  },
  {
    name: "Estimated number of shear zones",
    desc: "approximately 9 (1,200 / 139 ≈ 8.6)",
  },
  {
    name: "Cumulative shear zone trace span",
    desc: "approximately 29 m (9 × 3.2)",
  },
  {
    name: "Cumulative apparent shear zone thickness",
    desc: "approximately 67 m (9 × 7.4)",
  },
  {
    name: "Estimated cumulative shear zone affected length",
    desc: "approximately 96 m (29 + 67)",
  },
  {
    name: "Estimated proportion of Poor–Very Poor rock (Class IV–V)",
    desc: "approximately 8.0%",
  },
];

const powerhouseCavern = [
  { name: "Powerhouse axis / inclination", desc: "N047/00" },
  { name: "Horizontal angle (between tunnel axis & strike)", desc: "14" },
  { name: "Shear zone trace span along tunnel", desc: "139.7 m" },
  {
    name: "Apparent shear zone spacing along tunnel",
    desc: "357 m (TS / Asf = 75 / 0.21)",
  },
  {
    name: "Apparent thickness of the shear zone along cavern axis",
    desc: "19 m (Thickness / Asf = 4 / 0.21)",
  },
  {
    name: "Shear zones along the 85 m long powerhouse",
    desc: "unlikely under apparent spacing conditions",
  },
  {
    name: "Worst-case scenario",
    desc: "if a shear zone intersects the cavern and continues along its full length, the proportion of Poor–Very Poor (Rock Class IV–V) zones along the cavern length would be 100%",
  },
  {
    name: "Engineering implication",
    desc: "based on exploratory drift data, if the shear zone runs along a substantial length of the cavern, the powerhouse siting should be optimised for avoiding such a shear zone",
  },
];

const surgeTankAssessment = [
  { name: "Estimated span of shear zone within tank", desc: "9.6 m" },
  { name: "Apparent Spacing Factor (Asf)", desc: "0.53" },
  {
    name: "Apparent shear zone spacing along the tank",
    desc: "142 m (TS / Asf = 75 / 0.53)",
  },
  {
    name: "Apparent shear zone thickness",
    desc: "7.6 m (Thickness / Asf = 4 / 0.53)",
  },
  {
    name: "Shear zones along the 37.5 m deep tank",
    desc: "unlikely under average spacing conditions",
  },
  {
    name: "Trace span as a proportion of tank depth",
    desc: "25.6% (9.6 / 37.5). This represents the discontinuity trace span alone, not the total shear zone affected length. The latter would also depend on apparent thickness along the shaft.",
  },
];

const methodologySteps = [
  {
    name: "Step 1",
    desc: "The dominant discontinuity sets are established through stereonet analysis using DIPS (Figure 10).",
  },
  {
    name: "Step 2",
    desc: "The representative shear zone belonging to Discontinuity Set J2 is projected individually onto each underground component using Xlog3D. The tool generates excavation-specific 3D Discontinuity Logs and computes relevant parameters, particularly ChMin, ChMax and the Apparent Spacing Factor (Asf). Trace span is calculated as ChMax − ChMin.",
  },
  {
    name: "Step 3",
    desc: "The field-derived true spacing is converted into apparent spacing along each underground component using the Xlog3D-computed Apparent Spacing Factor (Asf).",
  },
  {
    name: "Step 4",
    desc: "The apparent spacing is used to estimate the likely number of shear zones intersecting each underground component by dividing the component length by the apparent spacing.",
  },
  {
    name: "Step 5",
    desc: "The apparent thickness of the shear zone along the excavation is computed as True Thickness divided by Asf. The cumulative apparent thickness is obtained by multiplying the apparent thickness by the estimated number of shear zones.",
  },
  {
    name: "Step 6",
    desc: "The sum of cumulative span and cumulative apparent thickness of the shear zones is expressed as the estimated proportion of discontinuity-controlled Poor and Very Poor rock along each underground component, viz. cumulative length and percentage of the component length. This is an obligatory requirement in any DPR.",
  },
];

const orientationReviewResults = [
  {
    name: "Source 3D Geological Log",
    desc: "DipDir N145; DipAmt 40; Ch. at Left SPL, Right SPL, Centreline.",
  },
  {
    name: "Xlog3D Generated 3D Discontinuity Log",
    desc: "DipDir N187; DipAmt 65; Ch. at Left SPL, Right SPL, Centreline from source log used.",
  },
];

const shearCompareResults = [
  {
    name: "Horizontal",
    desc: "Trace span 26.3 m; apparent thickness 38.5 m; total shear zone affected length 64.8 m.",
  },
  {
    name: "Inclined",
    desc: "Trace span 56.9 m; apparent thickness 111.1 m; total shear zone affected length 168.0 m.",
  },
  {
    name: "Drop Shaft",
    desc: "Trace span 7.6 m; apparent thickness 15.6 m; total shear zone affected length 23.2 m.",
  },
];

// Title page, rendered above the sections and kept out of the contents list.
export const helpTitlePage = {
  title: "Xlog3D User Manual",
  subtitle: "A Complete Guide to Xlog3D – The Web Tool",
  edition: "Edition 1.0 | October 2026",
  welcome: "Welcome to a new dimension in engineering geological analysis!",
};

export const helpSections = [
  {
    id: "preface",
    number: "",
    title: "Preface",
    items: [
      "Discontinuities are of critical importance in governing the behavior of a rock mass as an excavation medium. Their influence may arise from the dominant discontinuity set or sets at a given site, or from individual incompetent weaknesses such as shear and fault zones. A thick, water-charged shear or fault zone can be a tunneller’s nightmare — capable of triggering major instability or collapse during excavation, sometimes even after the tunnel has advanced beyond it. The Silkyara Road Tunnel and the Vishnugad-Pipalkoti Hydroelectric Project are two recent reminders of how serious such tunnelling hazards can become.",
      "Xlog3D has been developed to assist engineering geologists and underground engineers in analysing and transforming discontinuity data into excavation-scale engineering geological assessments.",
      "This browser-based tool can help with:",
      "Taking discontinuity sets derived from stereographic analysis, such as those from DIPS, to the next level — from structural analysis to engineering geological analysis throughout the lifecycle of a project.",
      "Generating excavation-specific 3D discontinuity logs.",
      "Computing excavation-specific discontinuity parameters.",
      "Computing apparent spacing and thickness of critical discontinuities and weak zones along proposed excavations.",
      "Projecting weak zones onto underground excavations.",
      "Projecting weak zones beyond tunnel headings.",
      "Reconstructing 3D geology from face logs.",
      "Computing discontinuity orientation where compass measurements are unreliable.",
      "Validating discontinuity orientation in 3D log records.",
      "Optimising tunnel and cavern layouts.",
      "Estimating poor rock class proportions during DPR investigations.",
      "Serving as a training and help tool for budding engineering geologists and underground engineers to visualise discontinuity geometry and generate more reliable field data.",
      "And much more…",
    ],
  },
  {
    id: "why",
    number: "1",
    title: "Why Xlog3D?",
    intro:
      "Engineering geological investigations for underground projects commonly establish dominant discontinuity sets through structural mapping, discontinuity measurements, and stereonet analysis using tools such as DIPS. These analyses define the structural framework of the rock mass and provide the foundation for engineering geological interpretation.",
    items: [
      "During DPR investigations and construction, however, engineers require answers to questions that extend beyond discontinuity orientation and kinematic assessment.",
    ],
    subsections: [
      {
        id: "why-approach",
        number: "1.2",
        title: "The Xlog3D Approach",
        subsections: [
          {
            id: "why-log",
            number: "1.2.1",
            title: "3D Discontinuity Log",
            items: [
              "A conventional 3D Geological Log is a developed record of the geology exposed along an underground excavation, documenting geological features such as lithology, geological structures and seepage, and their disposition on the excavation surface.",
              "In contrast, the 3D Discontinuity Log generated by Xlog3D is a geometric representation of discontinuity traces on the excavation surface, constructed from the input geological and excavation data. It may represent discontinuities already encountered or project discontinuities onto proposed excavations and beyond tunnel headings.",
              "Developed View: The tunnel surface is unfolded onto a 2D plane, with the walls on either side and the crown in the middle. A cross-sectional view of the tunnel at the far end illustrates the unfolding procedure.",
              "Measurement Caution: Unfolding of the tunnel surface leads to distortion of the crown portion. Measurements across the developed crown therefore require caution; straight lines joining discontinuity intersections at the spring lines or invert do not represent the true discontinuity orientation.",
            ],
          },
          {
            id: "why-approach-dpr",
            number: "1.2.2",
            title: "During DPR Investigations, Xlog3D:",
            items: [
              "Projects dominant discontinuity sets established through stereonet analysis onto proposed underground excavations.",
              "Generates excavation-specific three-dimensional discontinuity logs.",
              "Predicts excavation-specific discontinuity behaviour.",
              "Helps estimate discontinuity-controlled Poor and Very Poor rock stretches.",
              "Supports engineering geological assessment for DPR investigations, design optimisation and construction planning.",
            ],
          },
          {
            id: "why-approach-construction",
            number: "1.2.3",
            title: "During Construction, Xlog3D:",
            items: [
              "Analyses individual planar geological features encountered underground, particularly shear zones, faults and crushed rock bands.",
              "Projects weak zones beyond tunnel headings.",
              "Computes and validates discontinuity orientation.",
              "Reconstructs discontinuity geometry from traces recorded in two tunnel face logs.",
              "Supports engineering geological interpretation and construction planning.",
            ],
          },
        ],
      },
      {
        id: "why-essence",
        number: "1.3",
        title: "In Essence",
        items: [
          "Structural analytical tools like DIPS convert structural measurements into orientation- and kinematics-based understanding.",
          "Xlog3D converts that structural information into excavation-scale engineering geological intelligence for DPR-stage investigation, design and construction.",
          "Xlog3D is therefore much more than a three-dimensional logging tool. It is an engineering geological framework that transforms discontinuity information into practical engineering intelligence and bridges the gap between discontinuity-related geological interpretation and excavation-scale engineering geological assessment.",
          "Throughout this User Manual, the term tunnel includes tunnels, drifts, caverns and shafts unless stated otherwise.",
        ],
      },
      {
        id: "why-terminology",
        number: "1.4",
        title: "Salient Terminology",
        intro:
          "The Xlog3D terminology is explained through a typical 3D Discontinuity Log for a D-shaped N120 oriented horizontal tunnel (Figure 1). The plotted discontinuities include J1: N150/30; J2: N030/50, and J3: N340/20.",
        image: {
          src: fig01,
          alt: "Figure 1: A typical 3D discontinuity log for 3 ruling joint sets",
        },
        subsections: [
          {
            id: "why-terminology-input",
            number: "1.4.1",
            title: "Input Terminology",
            items: inputTerminology,
          },
          {
            id: "why-terminology-output",
            number: "1.4.2",
            title: "Output Terminology",
            items: outputTerminology,
            caution:
              "Discontinuity span may be used with care in consideration of the discontinuity persistence. Alerts are given in special cases like discontinuity trace parallel to the tunnel, or trace clipped at the log ends if it exceeds Start Ch. and/or End Ch., etc.",
          },
        ],
      },
    ],
  },
  {
    id: "apps",
    number: "2",
    title: "Salient Engineering Geological Applications",
    intro:
      "Xlog3D supports engineering geological assessment throughout the lifecycle of an infrastructure project, from inception to operation. While the generation of 3D Discontinuity Logs forms the foundation of the framework, its principal strength lies in transforming discontinuity-related geological information into engineering geological intelligence for practical engineering decision-making.",
    items: ["The principal engineering geological applications of Xlog3D are described below."],
    subsections: [
      {
        id: "apps-params",
        number: "2.1",
        title: "Computation of Excavation-specific Discontinuity Parameters",
        items: [
          "Plots 3D trace of the discontinuity and computes excavation-specific parameters including: apparent dip; angle between discontinuity strike and excavation direction; trace disposition – key chainages of the discontinuity trace on excavation surface; Apparent Spacing Factor (Asf) for computing apparent spacing and thickness of the discontinuity along tunnel; and trace span.",
          "Some of these parameters are significant in engineering geological analysis and difficult to visualise or quantify using conventional structural geological methods.",
        ],
      },
      {
        id: "apps-projection",
        number: "2.2",
        title: "Projection of Weak Zones Beyond Tunnel Headings",
        items: [
          "Projects individual weak zones encountered during construction beyond the exposed tunnel heading, enabling anticipation of geological conditions ahead of excavation and facilitating advance planning of excavation sequence, support measures and construction strategy.",
          "Example: projection of a shear zone dipping N240/30 that is intersected at Ch.20 face/heading of a tunnel advancing in N090 direction. Based on advance probing, the apparent thickness of the shear zone along the tunnel axis is estimated to be 10.6 m.",
          "The Xlog3D-generated 3D Discontinuity Log for the down-dip boundary of the shear zone (Figure 2) gives a trace span of 17.8 m (ChMax − ChMin) and an Apparent Spacing Factor (Asf) of 0.43. The apparent thickness of 10.6 m along the tunnel axis is based on advance probing; the corresponding true thickness is approximately 4.6 m (10.6 × 0.43).",
          "The generated 3D Discontinuity Log (Figure 2) is then exported to a drafting tool such as AutoCAD, where the shear zone is filled with an appropriate geological pattern for visual clarity (Figure 3).",
          "The estimated total weak-zone affected length along the tunnel is 28.4 m, comprising the 17.8 m trace span and 10.6 m apparent thickness. The shear zone first appears at Ch. 17.8 m at the left invert and is expected to exit at approximately Ch. 46.2 m. With the heading at Ch. 20 m, the zone first appears 2.2 m behind the heading and is expected to continue approximately 26.2 m beyond it.",
        ],
        subsections: [
          {
            id: "apps-projection-fig2",
            number: "",
            title: "Generated 3D discontinuity log",
            image: {
              src: fig02,
              alt: "Figure 2: Generated 3D discontinuity log",
            },
          },
          {
            id: "apps-projection-fig3",
            number: "",
            title: "Shear zone with filled-in geological pattern",
            image: {
              src: fig03,
              alt: "Figure 3: Shear zone with filled-in geological pattern",
            },
          },
        ],
      },
      {
        id: "apps-orientation",
        number: "2.3",
        title: "Computation of Discontinuity Orientation",
        subsections: [
          {
            id: "apps-orientation-tunnel",
            number: "2.3.1",
            title: "Orientation Computation from Known Chainages in a Tunnel",
            image: {
              src: fig04,
              alt: "Figure 4: Computation and log generation from discontinuity trace intersection data",
            },
            items: [
              "Determines discontinuity orientation from three-dimensional tunnel observations, providing an independent geometrical check where underground compass measurements are uncertain or affected by magnetic interference.",
              "In the example illustrated in Figure 4, the discontinuity trace intersects the tunnel at the Left SPL, Right SPL and Centreline at Crown at Ch. 27.9, 21.1 and 18.2, respectively. From these chainages, Xlog3D computes the discontinuity orientation as N173/35 and generates the corresponding 3D Discontinuity Log.",
              "A separate web tool – DisDip, is available at www.dhawanacademy.com for computing discontinuity orientation from known chainages of the trace in a tunnel.",
            ],
          },
          {
            id: "apps-orientation-log",
            number: "2.3.2",
            title: "Orientation Computation from Known Chainages in a 3D Log",
            items: [
              "Supports validation of discontinuity orientation and trace disposition in geological logs by reviewers, thereby improving the consistency and reliability of engineering geological interpretation.",
              "In the example below, a 3D Log with recorded discontinuity orientation is reviewed by computing the trace chainages in the log and plotting the discontinuity trace using Xlog3D.",
              "Source 3D Geological Log of a horseshoe-shaped tunnel of dimensions 11.38 m (height), 11.38 m (width at SPL), 8.35 m (width at invert), 5.69 m (wall height) aligned in N159 (Figure 5).",
              "Figure 6 is the Xlog3D generated 3D Discontinuity Log of the shear zone in the source log intersecting the tunnel centreline at crown at Ch. 293 m. The discontinuity orientation for log generation is computed from chainages at left SPL, right SPL, and centreline in the source log.",
              "A separate web tool – DisDip, is available at www.dhawanacademy.com for computing discontinuity orientation from known chainages of the trace in a 3D log.",
              "The review results are as follows:",
              ...orientationReviewResults.map((r) => `${r.name}: ${r.desc}`),
              "Review note: a substantial difference is found in the shear zone orientation – both in dip direction and dip amount. The error in the source log could be due to measurements using a compass.",
            ],
            subsections: [
              {
                id: "apps-orientation-log-fig5",
                number: "",
                title: "Source 3D geological log",
                image: {
                  src: fig05,
                  alt: "Figure 5: Source 3D geological log",
                },
              },
              {
                id: "apps-orientation-log-fig6",
                number: "",
                title: "Xlog3D generated 3D log of the shear zone",
                image: {
                  src: fig06,
                  alt: "Figure 6: Xlog3D generated 3D log of the shear zone",
                },
              },
            ],
          },
        ],
      },
      {
        id: "apps-face-logs",
        number: "2.4",
        title: "Generation of 3D Discontinuity Logs from Face Logs",
        items: [
          "Xlog3D uses traces of the same planar discontinuity recorded on two tunnel faces to reconstruct its three-dimensional orientation and generate the corresponding 3D Discontinuity Log.",
          "This application is particularly relevant where construction-stage geological records are limited to Face Logs. Xlog3D uses selected points on the mapped traces to reconstruct the discontinuity plane geometrically, without requiring direct underground compass measurement.",
          "The example below illustrates two face logs at 3 m spacing – Ch. 76 and Ch. 79, with apparent dip and side of the fall of the discontinuity trace, and location of the data points in these logs with reference to centreline (Figure 7).",
          "The discontinuity dip direction and amount computed from the input data are N057/33 (Figure 8).",
          "The 3D discontinuity log generated from the computed discontinuity orientation is illustrated in Figure 9.",
          "A separate web tool – DisDip, is available at www.dhawanacademy.com for computing discontinuity orientation from two face logs in a tunnel.",
        ],
        subsections: [
          {
            id: "apps-face-logs-fig7a",
            number: "",
            title: "Face-1 at Ch. 76",
            image: {
              src: fig07a,
              alt: "Figure 7: Face logs in the example — Face-1 at Ch. 76",
            },
          },
          {
            id: "apps-face-logs-fig7b",
            number: "",
            title: "Face-2 at Ch. 79",
            image: {
              src: fig07b,
              alt: "Figure 7: Face logs in the example — Face-2 at Ch. 79",
            },
          },
          {
            id: "apps-face-logs-fig8",
            number: "",
            title: "Computed discontinuity orientation from face logs",
            image: {
              src: fig08,
              alt: "Figure 8: Computed discontinuity orientation from face logs",
            },
          },
          {
            id: "apps-face-logs-fig9",
            number: "",
            title: "3D discontinuity log from face logs",
            image: {
              src: fig09,
              alt: "Figure 9: 3D discontinuity log from face logs",
            },
          },
        ],
      },
      {
        id: "apps-assessment",
        number: "2.5",
        title: "Excavation-scale Engineering Geological Assessment",
        items: [
          "Another significant application of Xlog3D is the estimation of the proportion of discontinuity-specific Poor and Very Poor rock stretches along an underground component.",
          "This forms a critical component of the Rock Mass Classification in the statutory requirement in an Indian Detailed Project Report (DPR) for hydropower and pumped storage projects.",
          "The methodology is illustrated below using a representative hydropower project in the Himalaya.",
        ],
        subsections: [
          {
            id: "apps-assessment-example",
            number: "2.5.1",
            title: "Illustrative Example — Application to a DPR Investigation for a Hydropower Project in the Himalaya",
            subsections: [
              {
                id: "apps-assessment-features",
                number: "2.5.1.1",
                title: "Salient Features",
                items: [
                  "The methodology adopted in Xlog3D is illustrated using a representative hydropower project in the Himalaya. The geological and geometric parameters have been simplified to demonstrate the methodology and engineering interpretation without affecting the underlying engineering geological principles.",
                  "The project comprises a headrace tunnel, pressure shaft, surge tank and underground powerhouse. Structural geological investigations established three dominant discontinuity sets within the project area.",
                  "J1 (foliation): N042/41",
                  "J2 (transverse): N123/58",
                  "J3 (longitudinal): N247/58",
                  "Field investigations further indicated that the principal shear zones were associated with Discontinuity Set J2; generally 3–5 m thick (average 4 m); occurring at an average true spacing of about 75 m. True spacing was estimated using TrueSpace — a free web tool available at www.dhawanacademy.com.",
                  "Headrace tunnel (HRT): 4,410 m long, 3.6 m × 3.6 m inverted D-shaped.",
                  "Surge tank: 37.5 m deep, 6 m diameter.",
                  "Pressure shaft: 1,200 m long, 3.6 m × 3.6 m inverted D-shaped.",
                  "Underground powerhouse: 35 m high × 15.5 m wide × 85 m long.",
                ],
              },
              {
                id: "apps-assessment-methodology",
                number: "2.5.1.2",
                title: "Methodology",
                intro: "The engineering geological assessment is carried out in six steps.",
                image: {
                  src: fig10,
                  alt: "Figure 10: Dominant discontinuity sets (with component orientations)",
                },
                items: methodologySteps,
              },
              {
                id: "apps-assessment-components",
                number: "2.5.1.3",
                title: "Engineering Geological Assessment of Underground Components",
                intro:
                  "The engineering geological assessment of the principal underground components is summarised below.",
                subsections: [
                  {
                    id: "apps-hrt-desander",
                    number: "2.5.1.3.1",
                    title: "900 m long HRT (Desander to Adit)",
                    image: {
                      src: fig11,
                      alt: "Figure 11: 3D log of ruling discontinuity sets",
                    },
                    items: hrtDesanderAdit,
                  },
                  {
                    id: "apps-hrt-adit-surge",
                    number: "2.5.1.3.2",
                    title: "3,510 m long HRT (Adit to Surge Tank)",
                    image: {
                      src: fig12,
                      alt: "Figure 12: 3D log of ruling discontinuity sets",
                    },
                    items: hrtAditSurge,
                  },
                  {
                    id: "apps-pressure-shaft",
                    number: "2.5.1.3.3",
                    title: "1,200 m long inclined pressure shaft",
                    image: {
                      src: fig13,
                      alt: "Figure 13: 3D log of ruling discontinuity sets",
                    },
                    items: pressureShaftAssessment,
                  },
                  {
                    id: "apps-powerhouse",
                    number: "2.5.1.3.4",
                    title: "Powerhouse cavern (long axis)",
                    image: {
                      src: fig14,
                      alt: "Figure 14: 3D log of ruling discontinuity sets",
                    },
                    items: powerhouseCavern,
                  },
                  {
                    id: "apps-surge-tank",
                    number: "2.5.1.3.5",
                    title: "Surge tank",
                    image: {
                      src: fig15,
                      alt: "Figure 15: 3D log of ruling discontinuity sets",
                    },
                    items: surgeTankAssessment,
                  },
                ],
              },
              {
                id: "apps-assessment-significance",
                number: "2.5.1.4",
                title: "Engineering Significance",
                items: [
                  "The example illustrates how Xlog3D extends stereonet-based structural interpretation to excavation-scale engineering geological assessment. By integrating discontinuity geometry with field-derived spacing, the framework enables objective estimation of discontinuity-specific Poor and Very Poor rock stretches during DPR investigations. This provides engineering geologists with a rational basis for anticipating adverse ground conditions, optimising underground layouts, planning support requirements and reducing geological uncertainty before construction.",
                ],
              },
            ],
          },
        ],
      },
      {
        id: "apps-layouts",
        number: "2.6",
        title: "Optimising Engineering Layouts of Tunnels and Caverns",
        intro:
          "Engineering geological analysis and assessment of significant discontinuities can help optimise the layout of tunnels, shafts and caverns. The following examples illustrate this capability of Xlog3D.",
        subsections: [
          {
            id: "apps-layouts-shear",
            number: "2.6.1",
            title: "A 10 m Thick Shear Intersecting a Pressure Shaft",
            items: [
              "Consider a 10 m thick shear zone dipping N040/50 and intersecting a proposed pressurised water conductor system, aligned in N110 direction.",
              "A comparative engineering geological analysis of this shear zone for alternative layouts comprising a horizontal pressure shaft, inclined pressure shaft and drop shaft can help the design team identify an optimised layout for crossing the shear zone.",
              "The 3D discontinuity logs of the shear zone for different alternatives of horizontal pressure shaft, inclined pressure shaft, and drop shaft are given in Figure 16, Figure 17 and Figure 18, respectively. While shear-1 represents the downdip (upper) surface of the fault zone, shear-1a represents the up-dip (lower) surface.",
            ],
            subsections: [
              {
                id: "apps-layouts-horizontal",
                number: "2.6.1.1",
                title: "Horizontal Pressure Shaft",
                image: {
                  src: fig16,
                  alt: "Figure 16: 3D discontinuity log of the shear zone",
                },
              },
              {
                id: "apps-layouts-inclined",
                number: "2.6.1.2",
                title: "Inclined Pressure Shaft",
                image: {
                  src: fig17,
                  alt: "Figure 17: 3D discontinuity log of the shear zone",
                },
                subsections: [
                  {
                    id: "apps-layouts-inclined-io",
                    number: "",
                    title: "Inclined pressure shaft — input and output table",
                    image: {
                      src: fig17a,
                      alt: "Input and output table for the inclined pressure shaft shear-zone example",
                    },
                  },
                ],
              },
              {
                id: "apps-layouts-drop",
                number: "2.6.1.3",
                title: "Drop Shaft",
                image: {
                  src: fig18,
                  alt: "Figure 18: 3D discontinuity log of the shear zone",
                },
              },
              {
                id: "apps-layouts-conclusions",
                number: "2.6.1.4",
                title: "Conclusions and Recommendation",
                items: [
                  "Engineering geological analysis using Xlog3D gives the following comparative results:",
                  ...shearCompareResults.map(
                    (r) => `${r.name}: ${r.desc}`
                  ),
                  "The estimated total shear zone affected length is lowest for the drop shaft (23.2 m), compared with the horizontal option (64.8 m) and inclined option (168.0 m). These totals comprise the calculated discontinuity trace span and apparent thickness along each component.",
                  "For the geometry considered in this example, the engineering geological assessment therefore favours a layout in which the shear zone is crossed by the drop shaft. Subject to hydraulic, structural, construction and other design considerations, the pressurised water-conductor layout may accordingly be optimised to minimise its interaction with the shear zone.",
                  "Workflow advice: The 3D discontinuity log generated by Xlog3D shows the schematic traces of the downdip (upper surface) and up-dip (lower surface) boundaries of the shear zone on the excavation surface. While Xlog3D allows generation of one trace at a time with provision to add traces, for depicting the full width of the shear zone the following workflow may be adopted:",
                  "Plot the trace of the downdip surface (upper surface).",
                  "Compute the apparent thickness using the Asf (Apparent Spacing/Thickness Factor).",
                  "Using the Add Trace feature, plot a parallel trace (up-dip or lower surface) in the up-dip direction at a distance equal to the apparent thickness.",
                  "Export the completed log to a tool like AutoCAD for final drafting, including appropriate geological symbols, annotations and legend in accordance with standard practice.",
                ],
              },
            ],
          },
          {
            id: "apps-layouts-fault",
            number: "2.6.2",
            title: "A Thick Fault Across a Long Tunnel",
            items: [
              "Consider a thick fault zone cutting across a long tunnel. In this example, a 20 m thick fault zone with crushed and gougy material and dipping N180/60 cuts across a D-shaped road tunnel. The tunnel is 8 m high × 8 m wide, 6 m wall height, and is aligned in N120 direction. The field engineering geological mapping and interpretations project the up-dip boundary of the fault zone (lower surface) at Ch. 725 of the tunnel.",
              "The Xlog3D-generated 3D Discontinuity Log for the up-dip fault boundary gives a trace span of 20.8 m (ChMax − ChMin) and an Asf of 0.43 (Figure 19, Figure 20). The apparent thickness of the 20 m thick fault zone is 46.5 m (20 / 0.43). The down-dip boundary (upper surface) is therefore projected 46.5 m farther along the tunnel.",
              "The estimated total fault zone affected length along the proposed tunnel is 67.3 m (20.8 + 46.5). The fault zone is expected to appear at Ch. 720.4 m at the right spring level and exit at Ch. 787.7 m at the left invert.",
              "Alternative study: The geology team considers a local realignment in the N180 direction, i.e. in the dip direction of the fault zone, to minimize the length affected by the fault zone. For this alternative (Figure 21, Figure 22), the calculated trace span is 4.6 m and apparent thickness is 23.0 m, giving a total fault zone affected length of 27.6 m. This is approximately 59% less than the 67.3 m estimated for the original N120 alignment.",
              "Recommendation: The geology team recommends considering a local realignment in the N180 direction, subject to other design requirements, to reduce the estimated fault zone affected length from 67.3 m to 27.6 m.",
              "Additionally, the team suggests advance probing for the actual positioning of the tunnel through TSP, probe drilling, etc. adequately before the estimated first intersection of the fault zone with the tunnel.",
            ],
            subsections: [
              {
                id: "apps-layouts-fault-fig19",
                number: "",
                title: "Fault zone along the proposed tunnel",
                image: {
                  src: fig19,
                  alt: "Figure 19: 3D log of the fault zone along the proposed tunnel",
                },
              },
              {
                id: "apps-layouts-fault-fig20",
                number: "",
                title: "Edited 3D log with filled-in symbols (proposed alignment)",
                image: {
                  src: fig20,
                  alt: "Figure 20: The edited 3D log of the fault zone with filled-in symbols (proposed alignment)",
                },
              },
              {
                id: "apps-layouts-fault-fig21",
                number: "",
                title: "Fault zone along the alternative alignment",
                image: {
                  src: fig21,
                  alt: "Figure 21: 3D log of the fault zone along the alternative alignment of the tunnel",
                },
              },
              {
                id: "apps-layouts-fault-fig22",
                number: "",
                title: "Edited 3D log with filled-in symbols (alternative alignment)",
                image: {
                  src: fig22,
                  alt: "Figure 22: The edited 3D log of the fault zone with filled-in symbols (alternative alignment)",
                },
              },
            ],
          },
        ],
      },
      {
        id: "apps-training",
        number: "2.7",
        title: "Training, Education and Capacity Building",
        subsections: [
          {
            id: "apps-training-young",
            number: "2.7.1",
            title: "Young 3D Geological Loggers",
            items: [
              "Xlog3D-generated 3D logs of these sets help young loggers visualize discontinuity geometry, understand trace disposition along tunnels and drifts, and generate more reliable and consistent field data.",
              "Helps them convert 2D Face Logs into 3D Logs.",
            ],
          },
          {
            id: "apps-training-education",
            number: "2.7.2",
            title: "Education",
            items: [
              "The web tool serves as an effective educational tool for understanding discontinuity behaviour in tunnels — an essential component of rock mass characterization.",
              "It can be integrated into academic curricula to support teaching engineering geology and underground excavation practices.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "working",
    number: "3",
    title: "Working Procedure",
    intro:
      "The Xlog3D workflow begins with selection of the excavation type and the plotting method.",
    items: [
      "Select the type of excavation: D-shaped Tunnel, Circular Tunnel, Horseshoe Tunnel, or Shaft (vertical).",
      "For tunnels, select the required plotting method:",
      "Trace from Known Orientation: Plot a discontinuity using its known orientation and location on the tunnel surface.",
      "Trace from Face Logs: Generate a 3D Discontinuity Log from discontinuity traces recorded on tunnel faces.",
      "Trace from Known Chainages: Plot a discontinuity from its known intersections at specified tunnel chainages or from an existing 3D Geological Log.",
      "Enter the required data in the Input interface table and generate the 3D Discontinuity Log.",
    ],
    subsections: [
      {
        id: "working-controls",
        number: "",
        title: "Plotting Controls",
        items: [
          {
            name: "Edit",
            desc: "Edit input values without losing the data already entered in the Input interface table.\nEdit input values while adding trace by selecting Trace 1.",
          },
          {
            name: "Add Trace",
            desc: "Edit the discontinuity orientation and data-point location for the next discontinuity and add the generated trace to the existing log. A changed trace colour and its data-point location help identify the added trace.",
          },
          {
            name: "Delete Trace",
            desc: "Delete a selected trace from the log. Click Delete Trace, select the trace to be removed, and click Delete.",
          },
          {
            name: "Download",
            desc: "Download the generated 3D Discontinuity Log in SVG, PNG, JPEG or PDF format.",
          },
          {
            name: "TrueSpace",
            desc: "The Output provides a link to the TrueSpace web tool for computing the true thickness and true spacing of a discontinuity or weak zone, such as a shear or fault zone (www.dhawanacademy.com).",
          },
        ],
      },
      {
        id: "input",
        number: "3",
        title: "Input Requirements",
        intro:
          "The inputs are listed below in the same sequence in which they are entered in the respective Xlog3D algorithms. Parameters common to all tunnel geometries are defined once, followed by geometry-specific inputs. Shaft inputs are listed separately.",
        subsections: [
          {
            id: "input-common",
            number: "3.1",
            title: "Common Tunnel Inputs",
            items: commonTunnelInputs,
            subsections: [
              {
                id: "input-face-logs",
                number: "",
                title: "Discontinuity Trace from Face Logs",
                items: faceLogInputs,
              },
            ],
          },
          {
            id: "input-geometry",
            number: "3.2",
            title: "Geometry-Specific Tunnel Inputs",
            subsections: [
              {
                id: "input-dshape",
                number: "3.2.1",
                title: "D-shaped Tunnel",
                items: dShapeInputs,
              },
              {
                id: "input-circular",
                number: "3.2.2",
                title: "Circular Tunnel",
                items: circularInputs,
              },
              {
                id: "input-horseshoe",
                number: "3.2.3",
                title: "Horseshoe Tunnel",
                items: horseshoeInputs,
              },
            ],
          },
          {
            id: "input-shaft",
            number: "3.3",
            title: "Shaft Inputs",
            items: shaftInputs,
          },
        ],
      },
    ],
  },
  {
    id: "output",
    number: "4",
    title: "Output Parameters",
    intro:
      "The 3D Discontinuity Log is the principal graphical output of Xlog3D. Parameters common to different tunnel geometries are defined below, followed by geometry-specific outputs.",
    subsections: [
      {
        id: "output-common",
        number: "4.1",
        title: "Common Tunnel Outputs",
        items: commonTunnelOutputs,
        caution:
          "The computed discontinuity trace span must be interpreted with due consideration of the persistence of the discontinuity.",
        subsections: [
          {
            id: "output-derived",
            number: "",
            title: "Derived Parameters",
            items: derivedOutputs,
            caution:
              "ChMin and ChMax are intended to represent the complete geometrically computed discontinuity trace, irrespective of the displayed log boundaries. Until the identified software issue concerning traces extending beyond the selected log limits is corrected, these values should be used with caution where the trace is clipped. Where practicable, select log limits that accommodate the complete trace.",
          },
        ],
      },
      {
        id: "output-geometry",
        number: "4.2",
        title: "Geometry-Specific Tunnel Outputs",
        subsections: [
          {
            id: "output-dshape",
            number: "4.2.1",
            title: "D-shaped Tunnel",
            items: [
              {
                name: "ChLI",
                desc: "Chainage at which the discontinuity trace intersects the left invert.",
              },
              {
                name: "ChRI",
                desc: "Chainage at which the discontinuity trace intersects the right invert.",
              },
            ],
          },
          {
            id: "output-circular",
            number: "4.2.2",
            title: "Circular Tunnel",
            items: [
              {
                name: "ChI",
                desc: "Chainage at Centreline at Invert where the discontinuity trace intersects. There is no left or right invert.",
              },
            ],
          },
          {
            id: "output-horseshoe",
            number: "4.2.3",
            title: "Horseshoe Tunnel",
            items: [
              {
                name: "ChLI",
                desc: "Chainage at which the discontinuity trace intersects the left invert.",
              },
              {
                name: "ChRI",
                desc: "Chainage at which the discontinuity trace intersects the right invert.",
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: "troubleshooting",
    number: "5",
    title: "Trouble Shooting",
    items: [
      "In case of a mis-click leading to undesired landing on the screen: refresh with Ctrl+Shift+R, or contact info@dhawanacademy.com (response time up to 48 hrs).",
    ],
  },
];

export function helpSectionElementId(sectionId) {
  return `help-sec-${sectionId}`;
}

export function helpItemId(sectionId, itemIndex) {
  return `help-item-${sectionId}-${itemIndex}`;
}

/**
 * Walk every leaf node (sections + nested subsections) and yield a flat list
 * with depth and a stable element id. Useful for both the TOC and search.
 */
export function flattenHelpSections(sections, depth = 0, out = []) {
  for (const s of sections) {
    out.push({ ...s, depth });
    if (s.subsections && s.subsections.length) {
      flattenHelpSections(s.subsections, depth + 1, out);
    }
  }
  return out;
}

const itemSearchText = (item) =>
  typeof item === "string" ? item : `${item.name} ${item.desc}`;

/**
 * One searchable chunk per bullet. Carries the leaf section id so search
 * results can scroll to the relevant heading.
 */
export function getHelpSearchChunks(sections) {
  const out = [];
  const flat = flattenHelpSections(sections);
  for (const s of flat) {
    if (!s.items) continue;
    s.items.forEach((item, itemIndex) => {
      const text = itemSearchText(item);
      out.push({
        id: helpItemId(s.id, itemIndex),
        sectionId: s.id,
        sectionTitle: s.title,
        text,
        searchText: `${s.title} ${text}`,
      });
    });
  }
  return out;
}
